import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { confirmAction, haptic, notify } from '../telegram'
import type { Chat, ChatMessage, Game } from '../types'

interface Props {
  game: Game
  onClose: () => void
}

/** Как часто спрашиваем сервер о новых сообщениях, пока чат открыт.
 *  Бот о сообщениях не пишет (решение владельца) — живость только здесь. */
const POLL_MS = 4000

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

function dayLabel(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === now.toDateString()) return t().today
  if (d.toDateString() === yesterday.toDateString()) return t().yesterday
  return d.toLocaleDateString(t().locale, { day: 'numeric', month: 'long' })
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(t().locale, { hour: '2-digit', minute: '2-digit' })
}

export default function GameChat({ game, onClose }: Props) {
  const [data, setData] = useState<Chat | null>(null)
  // Страницы более ранних сообщений, подгруженные вручную
  const [older, setOlder] = useState<ChatMessage[]>([])
  const [olderDone, setOlderDone] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [selected, setSelected] = useState<number | null>(null)
  const list = useRef<HTMLDivElement>(null)
  const lastRead = useRef(0)
  const lastSeen = useRef(0)

  const load = useCallback(async () => {
    try {
      setData(await api.chat(game.id))
    } catch (e) {
      // 403 — сняли с игры, пока чат был открыт
      notify(e instanceof Error ? e.message : 'Ошибка')
      onClose()
    }
  }, [game.id, onClose])

  useEffect(() => {
    void load()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const messages = useMemo(() => {
    const fresh = data?.messages ?? []
    const first = fresh[0]?.id ?? Infinity
    // Удалённое сообщение пропадает из свежей страницы; из ранних убираем
    // всё, что уже покрыто свежей страницей
    return [...older.filter((m) => m.id < first), ...fresh]
  }, [data, older])

  const lastId = messages.length ? messages[messages.length - 1].id : 0

  // Новое сообщение: отмечаем прочитанным и прокручиваем вниз — но только если
  // человек и так был внизу, а не читает переписку выше
  useEffect(() => {
    if (!lastId || lastId === lastSeen.current) return
    const el = list.current
    const first = lastSeen.current === 0
    const nearBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 120 : true
    lastSeen.current = lastId
    if (el && (first || nearBottom)) el.scrollTop = el.scrollHeight
    if (lastId > lastRead.current) {
      lastRead.current = lastId
      void api.readChat(game.id, lastId).catch(() => undefined)
    }
  }, [lastId, game.id])

  async function send() {
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    try {
      await api.sendChat(game.id, body)
      setText('')
      haptic()
      await load()
      if (list.current) list.current.scrollTop = list.current.scrollHeight
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSending(false)
    }
  }

  async function loadOlder() {
    const first = messages[0]
    if (!first) return
    try {
      const page = await api.chat(game.id, first.id)
      setOlder((prev) => [...page.messages, ...prev])
      if (!page.has_more) setOlderDone(true)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  async function act(fn: () => Promise<void>) {
    try {
      await fn()
      setSelected(null)
      await load()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  const pinned = data?.pinned ?? null
  const captain = data?.is_captain ?? false
  const hasEarlier = !olderDone && (older.length > 0 || (data?.has_more ?? false))
  let lastDay = ''

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().chat}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().doneClose}>
          ←
        </button>
        <div style={{ minWidth: 0 }}>
          <div className="title chat-title">{game.kind === 'game' ? t().chatGame : game.kind === 'training' ? t().chatTraining : t().chatTournament}</div>
          <div className="muted small chat-title">
            {formatWhen(game.starts_at)} · {game.club.name}
          </div>
        </div>
      </div>

      {pinned && (
        <div className="chat-pinned">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="small chat-pinned-label">📌 {t().chatPinned}</div>
            <div className="chat-pinned-text">{pinned.text}</div>
          </div>
          {captain && (
            <button className="link chat-unpin" onClick={() => act(() => api.pinChat(game.id, null))}>
              {t().chatUnpin}
            </button>
          )}
        </div>
      )}

      <div className="chat-list" ref={list}>
        {data === null && <p className="muted">{t().loading}</p>}
        {data && messages.length === 0 && (
          <div className="chat-empty muted">
            {t().chatEmpty}
            {captain && <div style={{ marginTop: 6 }}>{t().chatPinHint}</div>}
          </div>
        )}
        {hasEarlier && (
          <button className="link" style={{ width: '100%' }} onClick={loadOlder}>
            {t().chatEarlier}
          </button>
        )}

        {messages.map((m, i) => {
          const day = dayLabel(m.created_at)
          const showDay = day !== lastDay
          lastDay = day
          const previous = messages[i - 1]
          const grouped = !showDay && previous?.player_id === m.player_id
          const canDelete = m.mine || captain
          // Со своим сообщением — удалить; с чужим — пожаловаться или скрыть автора
          const canAct = true
          return (
            <div key={m.id}>
              {showDay && <div className="chat-day">{day}</div>}
              <div className={`chat-row${m.mine ? ' mine' : ''}${grouped ? ' grouped' : ''}`}>
                {!m.mine &&
                  (grouped ? (
                    <span className="chat-avatar-gap" />
                  ) : m.photo_url ? (
                    <img className="avatar chat-avatar" src={m.photo_url} alt="" />
                  ) : (
                    <span className="avatar chat-avatar">{initials(m.first_name)}</span>
                  ))}
                <button
                  className="chat-bubble"
                  aria-expanded={selected === m.id}
                  onClick={() => setSelected((id) => (id === m.id ? null : m.id))}
                >
                  {!m.mine && !grouped && <span className="chat-name">{m.first_name}</span>}
                  <span className="chat-text">{m.text}</span>
                  <span className="chat-time">{clock(m.created_at)}</span>
                </button>
              </div>
              {selected === m.id && canAct && (
                <div className={`chat-actions${m.mine ? ' mine' : ''}`}>
                  {captain && pinned?.id !== m.id && (
                    <button className="chip" onClick={() => act(() => api.pinChat(game.id, m.id))}>
                      📌 {t().chatPin}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className="chip"
                      onClick={async () => {
                        if (await confirmAction(t().chatDeleteConfirm)) await act(() => api.deleteChat(m.id))
                      }}
                    >
                      {t().chatDelete}
                    </button>
                  )}
                  {!m.mine && (
                    <button
                      className="chip"
                      onClick={async () => {
                        if (!(await confirmAction(t().chatReportConfirm))) return
                        await act(async () => {
                          await api.reportChat(m.id)
                          setOlder((prev) => prev.filter((x) => x.id !== m.id))
                          notify(t().chatReported)
                        })
                      }}
                    >
                      {t().chatReport}
                    </button>
                  )}
                  {!m.mine && (
                    <button
                      className="chip"
                      onClick={async () => {
                        if (!(await confirmAction(t().chatBlockConfirm(m.first_name)))) return
                        await act(async () => {
                          await api.blockPlayer(m.player_id)
                          setOlder((prev) => prev.filter((x) => x.player_id !== m.player_id))
                        })
                      }}
                    >
                      {t().chatBlock}
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <form
        className="chat-input"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        <input
          type="text"
          value={text}
          maxLength={1000}
          placeholder={t().chatPlaceholder}
          aria-label={t().chatPlaceholder}
          enterKeyHint="send"
          autoComplete="off"
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" disabled={sending || !text.trim()} aria-label={t().chatSend}>
          ➤
        </button>
      </form>
    </div>
  )
}
